import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {doc, getDoc} from 'firebase/firestore';
import {db} from '../firebase';
import {onAuthStateChanged, signOut} from 'firebase/auth';
import {auth} from '../firebase';
import {Text, Button, VStack, HStack, Box} from '@chakra-ui/react';
import {analytics} from '../services/analytics';
import {
  provisionUser,
  fetchOnboardingFacilities,
  selectOnboardingFacility,
  type OnboardingFacility,
} from '../services/onboardingService';
import {scribePreferences} from '../services/scribePreferencesService';
import {languageFavorites} from '../services/languageFavoritesService';
import {noteEditTracker} from '../services/noteEditTracker';
import {
  saveInterpreterAutoUnmuteOnConnect,
  saveProviderPhone,
  saveProviderProfile,
  splitProviderName,
  type ProviderProfileInput,
} from '../services/userProfileService';
import {
  clearCachedSuperAdminPreferences,
  resolveSuperAdminPreferences,
  saveSuperAdminPreferences,
  type SuperAdminPreferences,
} from '../services/superAdminPreferencesService';
import OnboardingModal from '../components/onboarding/OnboardingModal';
import type {UserData} from '../types';
import {withFirestoreTimeout} from '../services/firestoreTimeout';
import {readUserBootstrap} from '../services/userBootstrap';

const MICROSOFT_OIDC_PROVIDER_IDS = new Set([
  process.env.REACT_APP_SSO_AZURE_PROVIDER || 'oidc.opalite-entra',
  'oidc.azure-ad',
]);

const UserBootstrapError = ({
  onRetry,
  onSignOut,
}: {
  onRetry: () => void;
  onSignOut: () => void;
}) => (
  <Box
    minH="100vh"
    w="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    bg="gray.50"
    px={6}
  >
    <VStack
      spacing={4}
      maxW="440px"
      rounded="xl"
      borderWidth="1px"
      borderColor="gray.200"
      bg="white"
      boxShadow="sm"
      p={{base: 6, md: 8}}
      textAlign="center"
    >
      <Text fontSize="xl" fontWeight="semibold" color="gray.900">
        We couldn&apos;t finish loading your workspace
      </Text>
      <Text fontSize="sm" color="gray.600" lineHeight="tall">
        Your account is signed in, but the browser lost its connection while we
        were loading your profile. Try again, or sign out and return to the
        login screen.
      </Text>
      <HStack spacing={3} pt={2}>
        <Button variant="outline" onClick={onSignOut}>
          Sign out
        </Button>
        <Button variant="opalitePrimary" onClick={onRetry}>
          Try again
        </Button>
      </HStack>
    </VStack>
  </Box>
);

// Derives the structured profile fields from a raw `User` doc (or the
// equivalent backend-provisioned shape). New accounts store `first_name` /
// `last_name` directly; legacy accounts only have `provider_name`, so we
// backfill the split of that single field. `onboarding_completed` gates the
// first-time onboarding modal.
function deriveProviderProfileFields(data: any): {
  firstName: string;
  lastName: string;
  title: string;
  spokenLanguages: string[];
  onboardingCompleted: boolean;
  ehrProviderId: string | null;
  ehrIsResident: boolean;
  sessionSharingRestricted: boolean;
  interpreterAutoUnmuteOnConnect: boolean;
} {
  const fallback = splitProviderName(
    data?.provider_name || data?.providerName || '',
  );
  return {
    firstName: data?.first_name ?? fallback.first,
    lastName: data?.last_name ?? fallback.last,
    title: data?.provider_title || '',
    spokenLanguages: Array.isArray(data?.spoken_languages)
      ? data.spoken_languages
      : [],
    onboardingCompleted: data?.onboarding_completed === true,
    // eCW provider/resource id linked to this account (backfilled from the
    // EhrCache providers directory). Lets the planner scope the schedule by
    // EHR id instead of fuzzy provider-name matching.
    ehrProviderId:
      data?.ehr_provider_id ?? data?.ehrProviderId ?? data?.provider_id ?? null,
    ehrIsResident:
      data?.ehr_is_resident === true || data?.ehrIsResident === true,
    // Materialized by the backend from the company's Session Privacy config
    // (per-speciality + per-user rules). When true, every encounter this
    // provider creates is stamped `visibility: 'private'`.
    sessionSharingRestricted: data?.session_sharing_restricted === true,
    interpreterAutoUnmuteOnConnect:
      data?.interpreter_auto_unmute_on_connect === true,
  };
}

export interface UserContextValue {
  loading: boolean;
  userData: UserData | null;
  mockEhrMode: boolean;
  setMockEhrMode: (enabled: boolean) => Promise<void>;
  // Compatibility alias for the audio persistence guard. Mock EHR mode is the
  // only user-facing control and source of truth.
  demoMode: boolean;
  setDemoMode: (enabled: boolean) => Promise<void>;
  // Persist the provider's own phone number on their User doc and reflect it
  // in `userData` so the phone-encounter form prefill stays current.
  updateProviderPhone: (phone: string) => Promise<void>;
  setInterpreterAutoUnmuteOnConnect: (enabled: boolean) => Promise<void>;
  // Persist the first-time onboarding profile (name, title, spoken languages,
  // phone) and reflect it in `userData`, which also dismisses the blocking
  // onboarding modal.
  completeOnboarding: (
    profile: ProviderProfileInput,
    facilityId?: string,
  ) => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

// Super-admin accounts are internal tooling — explicitly suppress PostHog
// capture (events + session replay + autocapture) so internal QA traffic
// doesn't pollute product analytics. The opt-out state persists in
// localStorage, so once a super admin opts out on a device they stay
// opted out across reloads until a different (non-super-admin) user
// signs in on the same device.
function applyPosthogTrackingForRole(role: string | undefined) {
  try {
    const isSuperAdmin = role === 'superadmin';

    if (isSuperAdmin) {
      analytics.optOutCapturing();
    } else {
      // Silently re-enable capture — don't emit a $opt_in event so we
      // don't pollute the timeline when the SAME device flips between
      // a super admin and a regular user.
      analytics.optInCapturing({captureEventName: false});
    }
  } catch (error) {
    console.warn('Failed to apply PostHog tracking preference:', error);
  }
}

export function useUser(): UserContextValue {
  // The app always renders inside UserProvider.
  return useContext(UserContext) as UserContextValue;
}

export function UserProvider({children}: {children: React.ReactNode}) {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [mockEhrMode, setMockEhrModeState] = useState(false);
  const activePreferenceUserIdRef = React.useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [facilities, setFacilities] = useState<OnboardingFacility[]>([]);
  const [facilitySelectionRequired, setFacilitySelectionRequired] =
    useState(false);

  const retryBootstrap = useCallback(() => {
    setBootstrapError(false);
    setLoading(true);
    window.location.reload();
  }, []);

  const signOutAfterBootstrapFailure = useCallback(() => {
    void signOut(auth);
  }, []);

  // Facility/company names are display metadata, not authorization inputs.
  // Resolve them in parallel after the core User doc has opened the app so a
  // slow secondary Firestore stream cannot hold the entire workspace hostage.
  const hydrateTenantNames = (rawUser: any, userId: string) => {
    const facilityNamePromise = rawUser?.facility_id
      ? withFirestoreTimeout(getDoc(doc(db, 'Facility', rawUser.facility_id)), {
          ms: 8_000,
          label: 'user-bootstrap-facility-name',
        })
          .then(snapshot =>
            snapshot.exists()
              ? snapshot.data().facility_name || 'Unknown Facility'
              : null,
          )
          .catch(error => {
            console.warn('Could not hydrate facility name:', error);
            return null;
          })
      : Promise.resolve(null);
    const companyNamePromise = rawUser?.company_id
      ? withFirestoreTimeout(getDoc(doc(db, 'Company', rawUser.company_id)), {
          ms: 8_000,
          label: 'user-bootstrap-company-name',
        })
          .then(snapshot =>
            snapshot.exists()
              ? snapshot.data().name || 'Unknown Company'
              : null,
          )
          .catch(error => {
            console.warn('Could not hydrate company name:', error);
            return null;
          })
      : Promise.resolve(null);

    void Promise.all([facilityNamePromise, companyNamePromise]).then(
      ([facilityName, companyName]) => {
        if (auth.currentUser?.uid !== userId) return;
        setUserData(previous => {
          if (!previous || previous.userId !== userId) return previous;
          return {
            ...previous,
            ...(facilityName ? {facilityName} : {}),
            ...(companyName ? {companyName} : {}),
          };
        });
      },
    );
  };
  const hydrateMockEhrMode = (rawUser: any, userId: string) => {
    activePreferenceUserIdRef.current = userId;
    const preferences = resolveSuperAdminPreferences(
      rawUser?.role,
      userId,
      rawUser?.superAdminPreferences,
      rawUser?.super_admin_preferences,
    );
    setMockEhrModeState(preferences.mockEhrMode);
    setUserData(prev =>
      prev ? {...prev, superAdminPreferences: preferences} : prev,
    );
  };

  const setMockEhrMode = async (enabled: boolean) => {
    if (userData?.role !== 'superadmin' || !userData.userId) {
      throw new Error('Mock EHR Mode is only available to superadmins.');
    }
    const previous: SuperAdminPreferences = {
      mockEhrMode,
    };
    const next: SuperAdminPreferences = {mockEhrMode: Boolean(enabled)};
    setMockEhrModeState(next.mockEhrMode);
    setUserData(prev => (prev ? {...prev, superAdminPreferences: next} : prev));
    try {
      await saveSuperAdminPreferences(userData.userId, next, previous);
    } catch (error) {
      setMockEhrModeState(previous.mockEhrMode);
      setUserData(prev =>
        prev ? {...prev, superAdminPreferences: previous} : prev,
      );
      throw error;
    }
  };
  const setDemoMode = setMockEhrMode;

  const updateProviderPhone = async (phone: string) => {
    const userId = userData?.userId;
    if (!userId) return;
    const trimmed = String(phone || '').trim();
    setUserData(prev => (prev ? {...prev, providerPhone: trimmed} : prev));
    try {
      await saveProviderPhone(userId, trimmed);
    } catch (error) {
      console.warn('Failed to persist provider phone:', error);
    }
  };

  const setInterpreterAutoUnmuteOnConnect = async (enabled: boolean) => {
    const userId = userData?.userId;
    if (!userId) return;
    const previous = userData.interpreterAutoUnmuteOnConnect === true;
    const next = Boolean(enabled);
    setUserData(prev =>
      prev ? {...prev, interpreterAutoUnmuteOnConnect: next} : prev,
    );
    try {
      await saveInterpreterAutoUnmuteOnConnect(userId, next);
    } catch (error) {
      setUserData(prev =>
        prev ? {...prev, interpreterAutoUnmuteOnConnect: previous} : prev,
      );
      throw error;
    }
  };

  const completeOnboarding = async (
    profile: ProviderProfileInput,
    facilityId?: string,
  ) => {
    const userId = userData?.userId;
    if (!userId) return;
    let selectedFacility: OnboardingFacility | undefined;
    if (!userData.facilityId) {
      if (!facilityId) {
        throw new Error('Select your facility to finish setup.');
      }
      selectedFacility = facilities.find(
        facility => facility.id === facilityId,
      );
      if (!selectedFacility) {
        throw new Error('The selected facility is no longer available.');
      }
      await selectOnboardingFacility(facilityId);
    }
    const firstName = String(profile.firstName || '').trim();
    const lastName = String(profile.lastName || '').trim();
    const providerPhone = String(profile.providerPhone || '').trim();
    const spokenLanguages = (profile.spokenLanguages || [])
      .map(language => String(language).trim())
      .filter(Boolean);
    // Persist first so a failed write keeps the modal up rather than silently
    // dropping the user into the app without a saved profile.
    await saveProviderProfile(userId, {
      firstName,
      lastName,
      title: profile.title,
      providerPhone,
      spokenLanguages,
    });
    setUserData(prev =>
      prev
        ? {
            ...prev,
            firstName,
            lastName,
            title: String(profile.title || '').trim(),
            spokenLanguages,
            providerPhone: providerPhone || prev.providerPhone,
            displayName:
              [firstName, lastName].filter(Boolean).join(' ') ||
              prev.displayName,
            ...(selectedFacility
              ? {
                  facilityId: selectedFacility.id,
                  facilityName:
                    selectedFacility.facility_name || selectedFacility.name,
                }
              : {}),
            onboardingCompleted: true,
          }
        : prev,
    );
    if (selectedFacility) {
      setFacilities([]);
      setFacilitySelectionRequired(false);
      hydrateTenantNames(
        {...userData, facility_id: selectedFacility.id},
        userId,
      );
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (!user) {
        clearCachedSuperAdminPreferences(activePreferenceUserIdRef.current);
        activePreferenceUserIdRef.current = null;
        setUserData(null);
        setFacilities([]);
        setFacilitySelectionRequired(false);
        setMockEhrModeState(false);
        setBootstrapError(false);
        setLoading(false);
        // Defense-in-depth for shared devices: scribe prefs and
        // note-edit history are personal data (example notes can contain
        // real patient text). Wipe the localStorage layer on sign-out
        // so the next account that signs in here cannot read the
        // previous user's customizations through a stale cache. The
        // Firestore copy is the source of truth and will rehydrate
        // when the next user signs in (see the post-login sync effect
        // below).
        try {
          scribePreferences.clearLocalCache();
        } catch (_) {
          /* noop */
        }
        try {
          languageFavorites.clearLocalCache();
        } catch (_) {
          /* noop */
        }
        try {
          noteEditTracker.clearLocalCache?.();
        } catch (_) {
          /* noop */
        }
        return;
      }

      try {
        setLoading(true);
        setBootstrapError(false);
        const userDocRef = doc(db, 'User', user.uid);
        const userDoc = await readUserBootstrap(
          () => getDoc(userDocRef),
          'user-bootstrap-profile',
        );

        if (userDoc.exists()) {
          const data = userDoc.data();

          if (!data.facility_id && data.company_id) {
            let backendFacilities = [];
            try {
              backendFacilities = await fetchOnboardingFacilities();
              setFacilities(backendFacilities);
            } catch (err) {
              console.warn('Failed to fetch facilities from backend:', err);
              setFacilities([]);
            }

            if (backendFacilities.length === 1) {
              const sole = backendFacilities[0];
              await selectOnboardingFacility(sole.id);

              setUserData({
                facilityId: sole.id,
                companyId: data.company_id,
                facilityName: sole.facility_name || sole.name,
                companyName: data.company_name || 'Unknown Company',
                role: data.role,
                userId: user.uid,
                email: user.email || '',
                displayName:
                  data.provider_name ||
                  data.providerName ||
                  user.displayName ||
                  '',
                specialty: data.specialty || '',
                scribeDisabled: data.scribe_disabled === true,
                providerPhone: data.provider_phone || '',
                telephonicCode: data.telephonic_code || '',
                ...deriveProviderProfileFields(data),
              });
              hydrateMockEhrMode(data, user.uid);
              hydrateTenantNames({...data, facility_id: sole.id}, user.uid);
              setLoading(false);
              return;
            }

            setUserData({
              facilityId: '',
              companyId: data.company_id,
              facilityName: '',
              companyName: data.company_name || 'Unknown Company',
              role: data.role,
              userId: user.uid,
              email: user.email || '',
              displayName:
                data.provider_name ||
                data.providerName ||
                user.displayName ||
                '',
              specialty: data.specialty || '',
              scribeDisabled: data.scribe_disabled === true,
              providerPhone: data.provider_phone || '',
              telephonicCode: data.telephonic_code || '',
              ...deriveProviderProfileFields(data),
            });
            hydrateMockEhrMode(data, user.uid);
            setFacilitySelectionRequired(true);
            setLoading(false);
            return;
          }

          setUserData({
            facilityId: data.facility_id,
            companyId: data.company_id,
            facilityName: data.facility_name || 'Unknown Facility',
            companyName: data.company_name || 'Unknown Company',
            role: data.role,
            userId: user.uid,
            email: user.email || '',
            displayName:
              data.provider_name || data.providerName || user.displayName || '',
            specialty: data.specialty || '',
            scribeDisabled: data.scribe_disabled === true,
            providerPhone: data.provider_phone || '',
            telephonicCode: data.telephonic_code || '',
            ...deriveProviderProfileFields(data),
          });
          hydrateMockEhrMode(data, user.uid);
          hydrateTenantNames(data, user.uid);
        } else {
          console.log(
            'User document not found - provisioning via backend onboarding',
          );

          const isMicrosoftUser = user.providerData?.some(provider =>
            MICROSOFT_OIDC_PROVIDER_IDS.has(provider.providerId),
          );
          const provider = isMicrosoftUser ? 'microsoft' : 'email';

          try {
            const onboardingResult = await provisionUser({
              provider,
              email: user.email,
              providerName: user.displayName || undefined,
            });

            const provisionedUser = onboardingResult.user;
            console.log(
              'User provisioned via backend:',
              user.uid,
              provisionedUser.email,
            );

            if (onboardingResult.requires_facility_selection) {
              setFacilities(onboardingResult.facilities || []);
              setUserData({
                facilityId: '',
                companyId: provisionedUser.company_id,
                facilityName: '',
                companyName: provisionedUser.company_name || 'Unknown Company',
                role: provisionedUser.role,
                userId: user.uid,
                email: user.email || '',
                displayName:
                  provisionedUser.provider_name || user.displayName || '',
                specialty: provisionedUser.specialty || '',
                scribeDisabled: provisionedUser.scribe_disabled === true,
                providerPhone: provisionedUser.provider_phone || '',
                ...deriveProviderProfileFields(provisionedUser),
              });
              hydrateMockEhrMode(provisionedUser, user.uid);
              setFacilitySelectionRequired(true);
            } else {
              setUserData({
                facilityId: provisionedUser.facility_id,
                companyId: provisionedUser.company_id,
                facilityName:
                  provisionedUser.facility_name || 'Unknown Facility',
                companyName: provisionedUser.company_name || 'Unknown Company',
                role: provisionedUser.role,
                userId: user.uid,
                email: user.email || '',
                displayName:
                  provisionedUser.provider_name || user.displayName || '',
                specialty: provisionedUser.specialty || '',
                scribeDisabled: provisionedUser.scribe_disabled === true,
                providerPhone: provisionedUser.provider_phone || '',
                ...deriveProviderProfileFields(provisionedUser),
              });
              hydrateMockEhrMode(provisionedUser, user.uid);
              hydrateTenantNames(provisionedUser, user.uid);
            }
          } catch (onboardingError) {
            console.error('Backend onboarding failed:', onboardingError);
            setUserData(null);
            setMockEhrModeState(false);
            setBootstrapError(true);
            analytics.capture('user_bootstrap_failed', {
              stage: 'backend_onboarding',
              error_name: (onboardingError as any)?.name,
              error_message: String(
                (onboardingError as any)?.message || onboardingError,
              ).slice(0, 160),
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUserData(null);
        setMockEhrModeState(false);
        setBootstrapError(true);
        analytics.capture('user_bootstrap_failed', {
          stage: 'firestore_user_profile',
          error_name: (error as any)?.name,
          error_message: String((error as any)?.message || error).slice(0, 160),
        });
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Toggle PostHog capture whenever the resolved role changes. We watch
  // role rather than firing inside every setUserData branch above so that
  // logout (userData -> null) also opts the device back in for any
  // anonymous / login-page traffic, even if a super admin signed out on
  // the same browser earlier.
  useEffect(() => {
    applyPosthogTrackingForRole(userData?.role);
  }, [userData?.role]);

  // NOTE: the post-login EHR roster refresh (`/sync/me`) lives in
  // `PlannerPage` (the post-login landing screen, via useSameDayEhrSync),
  // not here. Centralizing it there lets the same trigger that fires the
  // sync also refetch the schedule when new same-day appointments come
  // back — and avoids a first-login race where a UserContext-fired sync
  // would `dedupe` the planner's call, hiding the freshly-minted rows
  // until the next refresh.

  // ── Post-login scribe-pref hydrate ─────────────────────────────
  // Pulls the user's saved customize-notes preferences (note length,
  // custom style instructions, per-template example notes) from
  // Firestore into the localStorage layer that `scribePreferencesService`
  // reads on the prompt hot path.
  //
  // Without this effect, a doctor who lands directly on a post-session
  // URL — notification deep link, EHR bookmark, "open in new tab" — sees
  // an empty NoteCustomizationModal even though their settings live
  // safely in `User/{uid}.scribePreferences`. The previous hydrate path
  // ran only on `ScribePage` mount, so cross-session persistence was
  // best-effort. Lifting it to UserContext makes prefs available
  // everywhere the user is signed in, on every device.
  //
  // The same tab-scoped Set used by the EHR effect (`triggeredPrefsSyncRef`)
  // dedupes StrictMode double-mounts in dev and account-switches in the
  // same tab — each (tab, user) pair runs exactly one hydrate.
  const triggeredPrefsSyncRef = React.useRef(new Set<string>());
  useEffect(() => {
    const userId = userData?.userId;
    if (!userId) return;
    if (triggeredPrefsSyncRef.current.has(userId)) return;
    triggeredPrefsSyncRef.current.add(userId);

    scribePreferences.syncFromFirebase(userId).catch(err => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Post-login scribe-pref sync threw:', err);
      }
    });
    languageFavorites.syncFromFirebase(userId).catch(err => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Post-login language-favorites sync threw:', err);
      }
    });
    noteEditTracker.syncFromFirebase?.(userId)?.catch?.(err => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Post-login note-edit-tracker sync threw:', err);
      }
    });
  }, [userData?.userId]);

  const value: UserContextValue = {
    loading,
    userData,
    mockEhrMode,
    setMockEhrMode,
    demoMode: mockEhrMode,
    setDemoMode,
    updateProviderPhone,
    setInterpreterAutoUnmuteOnConnect,
    completeOnboarding,
  };

  // Facility selection is part of this same blocking profile form. This keeps
  // first login to one coherent onboarding surface on every client.
  const shouldShowOnboarding =
    !loading &&
    !!userData &&
    (userData.onboardingCompleted !== true || facilitySelectionRequired);

  return (
    <UserContext.Provider value={value}>
      {bootstrapError ? (
        <UserBootstrapError
          onRetry={retryBootstrap}
          onSignOut={signOutAfterBootstrapFailure}
        />
      ) : (
        children
      )}
      {shouldShowOnboarding && userData && (
        <OnboardingModal
          isOpen
          userData={userData}
          facilities={facilities}
          facilityRequired={facilitySelectionRequired}
          onComplete={completeOnboarding}
        />
      )}
    </UserContext.Provider>
  );
}
